/**
 * SKILL.md manifest validator (schema_version 1).
 *
 * The locked schema is reconciled from the real built manifests in
 * `~/Downloads/MCP/04 - Skills Hub/Skills/**​/SKILL.md` (60 files) against the
 * Build-Guide "ideal" frontmatter. Only `skill_id`, `name`, `description`,
 * `tier`, and `locales` are required: every real manifest carries those.
 * Everything else is optional so the Default shelf validates today and authored
 * skills can grow into the richer fields.
 *
 * IMPORTANT: `tier` is FREE-FORM TEXT here. Real manifests use wildly
 * inconsistent values ("Plus", "plus", "free_basic_or_plus_full",
 * "Free 1/day, Plus unlimited", "Free | Plus | Pro", …). The canonical
 * entitlement tier (free|plus|pro) lives on the Skill row, normalized at import.
 *
 * Zero runtime deps: this package only ships zod, so the YAML frontmatter is
 * parsed by a small purpose-built reader that covers exactly the constructs the
 * manifests use (scalars, quoted scalars, `|`/`>` block scalars, inline
 * `[a, b]` flow arrays, and `- item` block lists). It is NOT a general YAML
 * parser.
 */
import { z } from 'zod';

export const SKILL_MANIFEST_SCHEMA_VERSION = 1 as const;

// ── Two-way skills: kind / run / cv_config (design: docs/skills-two-way-design.md)

/** Discriminator: absent/'default' = portable instruction skill. */
export const skillKindSchema = z.enum(['default', 'cv_custom']);
export type SkillKind = z.infer<typeof skillKindSchema>;

/** Drives the run panel instead of the hardcoded seed map. */
export const skillRunConfigSchema = z.object({
  input: z.enum(['cv', 'text', 'none']),
  output: z.enum(['cv_edit', 'markdown']),
});
export type SkillRunConfig = z.infer<typeof skillRunConfigSchema>;

/**
 * Existing render vocabulary only: header (name/title/contact) always
 * renders and is NOT a section. Mirrored as `CvSectionId` in @tabbio/types.
 */
export const CV_SECTION_IDS = [
  'summary',
  'careerHighlights',
  'skills',
  'experience',
  'projects',
  'education',
  'certifications',
  'languages',
] as const;
export const cvSectionIdSchema = z.enum(CV_SECTION_IDS);
export type CvSectionId = z.infer<typeof cvSectionIdSchema>;

export const SKILL_CV_SECTION_PROMPT_MAX = 2000;

/**
 * One entry in the manifest's ordered include-list. Accepts the `- skills`
 * shorthand (plain scalar) as well as the canonical `- id: skills` mapping.
 * An empty `prompt:` normalizes to "no prompt".
 */
const skillCvSectionSchema = z.preprocess(
  (v) => (typeof v === 'string' ? { id: v } : v),
  z.object({
    id: cvSectionIdSchema,
    prompt: z.preprocess(
      (v) => (v === '' || v === null ? undefined : v),
      z
        .string()
        .max(
          SKILL_CV_SECTION_PROMPT_MAX,
          `Section prompt must be at most ${SKILL_CV_SECTION_PROMPT_MAX} characters`,
        )
        .optional(),
    ),
  }),
);
export type SkillCvSectionConfig = z.infer<typeof skillCvSectionSchema>;

const upperString = (v: unknown) =>
  typeof v === 'string' ? v.toUpperCase() : v;

/** Tolerates the string booleans the mini-YAML reader can't produce but hand-edits can. */
const booleanish = z.preprocess((v) => {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return v;
}, z.boolean());

/**
 * `cv_config` block for `kind: cv_custom` skills. Style/color are the curated
 * `CVConfigData` presets (6 accent ids from @tabbio/tokens COLOR_MAP: no free
 * hex). `sections` is the ordered include-list; an omitted section is hidden.
 */
export const skillCvConfigSchema = z.object({
  style: z.preprocess(
    upperString,
    z.enum(['MODERN', 'CLASSIC', 'EXECUTIVE', 'CREATIVE']),
  ),
  color: z.preprocess(
    upperString,
    z.enum(['BLACK', 'BLUE', 'PURPLE', 'EMERALD', 'RED', 'ORANGE']),
  ),
  showPhoto: booleanish,
  sections: z
    .array(skillCvSectionSchema)
    .min(1, 'At least one section is required')
    .superRefine((sections, ctx) => {
      const seen = new Set<string>();
      sections.forEach((section, index) => {
        if (seen.has(section.id)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Duplicate section id: ${section.id}`,
            path: [index, 'id'],
          });
        }
        seen.add(section.id);
      });
    }),
});
export type SkillCvConfig = z.infer<typeof skillCvConfigSchema>;

// ── tabbio/v2 catalog fields (docs/TABBIO-SKILLS-DOCUMENTATION.md) ────────────

export const SKILL_FORMAT_V2 = 'tabbio/v2' as const;

export const skillTypeSchema = z.enum(['cv', 'other']);
export type SkillTypeValue = z.infer<typeof skillTypeSchema>;

export const skillCountrySchema = z.enum([
  'uae',
  'ksa',
  'kwt',
  'qat',
  'omn',
  'bhr',
  'gulf',
]);
export const skillLevelSchema = z.enum(['intern', 'fresh', 'mid', 'exec', 'all']);
export const skillLanguageSchema = z.enum(['en', 'ar', 'bi']);
export const skillCategorySchema = z.enum([
  'apply',
  'interview',
  'message',
  'general',
]);
export const skillRecommendedModelSchema = z.enum([
  'lite',
  'flash',
  'gpt',
  'sonnet',
  'gpro',
  'opus',
]);
export const skillContextSourceSchema = z.enum([
  'primary_cv',
  'profile',
  'memory',
]);

const lowerString = (v: unknown) =>
  typeof v === 'string' ? v.toLowerCase() : v;

/** CV-skill export layout: template + accent hex + market photo default. */
export const skillLayoutSchema = z.object({
  template: z.preprocess(
    lowerString,
    z.enum(['modern', 'classic', 'executive', 'creative']),
  ),
  accent: z
    .string()
    .regex(/^#[0-9a-fA-F]{3,8}$/, 'accent must be a hex color'),
  photo: z.preprocess((v) => {
    if (v === 'true') return true;
    if (v === 'false') return false;
    return v;
  }, z.boolean()),
});
export type SkillLayoutConfig = z.infer<typeof skillLayoutSchema>;

/** One declarative run-screen input (tabbio/v2 `inputs[]`). */
export const skillInputFieldSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  type: z
    .preprocess(lowerString, z.enum(['text', 'textarea', 'select']))
    .default('text'),
  required: z
    .preprocess((v) => {
      if (v === 'true') return true;
      if (v === 'false' || v === undefined || v === null || v === '')
        return false;
      return v;
    }, z.boolean())
    .default(false),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(),
});
export type SkillInputFieldConfig = z.infer<typeof skillInputFieldSchema>;

// ── Zod schema ────────────────────────────────────────────────────────────────

const optionalStringArray = z.preprocess(
  (v) => {
    if (v === undefined || v === null) return undefined;
    return Array.isArray(v) ? v.map((x) => String(x)) : [String(v)];
  },
  z.array(z.string()).optional(),
);

const localesSchema = z.preprocess(
  (v) => {
    if (v === undefined || v === null) return undefined;
    return Array.isArray(v) ? v.map((x) => String(x)) : [String(v)];
  },
  z.array(z.string()).min(1, 'At least one locale is required').optional(),
);

/**
 * Folds the template's singular `action_code` into `action_codes` before
 * validation so both spellings are accepted.
 */
const skillManifestObject = z
  .object({
    schema_version: z.coerce
      .number()
      .int()
      .optional()
      .default(SKILL_MANIFEST_SCHEMA_VERSION),
    // Required core. Only `name` + `description` are hard-required so a
    // foreign/portable SKILL.md (Claude/ChatGPT-style: name + description
    // frontmatter only) imports cleanly. Tabbio-built manifests still carry
    // skill_id/tier/locales; importers fill defaults when absent.
    skill_id: z.string().min(1).optional(),
    name: z.string().min(1, 'name is required'),
    description: z.string().min(1, 'description is required'),
    tier: z.string().min(1).optional(), // free-form, see file header
    locales: localesSchema,
    // Two-way skills (docs/skills-two-way-design.md).
    kind: skillKindSchema.optional(),
    run: skillRunConfigSchema.optional(),
    cv_config: skillCvConfigSchema.optional(),
    // Optional descriptive fields.
    skill_version: z.string().optional(),
    name_ar: z.string().optional(),
    description_ar: z.string().optional(),
    status: z.string().optional(),
    visibility: z.string().optional(),
    author: z.string().optional(),
    author_url: z.string().optional(),
    license: z.string().optional(),
    region: z.string().optional(),
    icon_key: z.string().optional(),
    icon_url: z.string().optional(),
    categories: optionalStringArray,
    tags: optionalStringArray,
    action_codes: optionalStringArray,
    action_code: z.string().optional(),
    // Per-skill tool allow-list.
    tools: optionalStringArray,
    scopes: optionalStringArray,
    md_alignment: optionalStringArray,
    subject_support: optionalStringArray,
    hand_off_from: optionalStringArray,
    hand_off_to: optionalStringArray,
    emits_events: optionalStringArray,
    resources: optionalStringArray,
    // Legacy manifests carry an object schema; tabbio/v2 carries the
    // declarative run-screen field list.
    inputs: z
      .union([z.array(skillInputFieldSchema), z.record(z.unknown())])
      .optional(),
    outputs: z.record(z.unknown()).optional(),
    privacy: z.record(z.unknown()).optional(),
    evals: z.unknown().optional(),
    eval_key: z.string().optional(),
    // tabbio/v2 catalog fields (aliases id/version/maker are folded in the
    // schema preprocess below).
    skill_format: z.string().optional(),
    type: z.preprocess(lowerString, skillTypeSchema).optional(),
    icon: z.string().optional(), // flag:<country> | cat:<category> | image:<asset>
    country: z.preprocess(lowerString, skillCountrySchema).optional(),
    level: z.preprocess(lowerString, skillLevelSchema).optional(),
    language: z.preprocess(lowerString, skillLanguageSchema).optional(),
    category: z.preprocess(lowerString, skillCategorySchema).optional(),
    recommended_model: z
      .preprocess(lowerString, skillRecommendedModelSchema)
      .optional(),
    context: z.array(skillContextSourceSchema).optional(),
    verified: booleanish.optional(),
    layout: skillLayoutSchema.optional(),
  })
  .passthrough();

export const skillManifestSchema = z
  .preprocess((val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      const obj = { ...(val as Record<string, unknown>) };
      if (obj.action_code != null && obj.action_codes == null) {
        obj.action_codes = Array.isArray(obj.action_code)
          ? obj.action_code
          : [obj.action_code];
      }
      // tabbio/v2 aliases → canonical keys (id/version/maker).
      if (obj.id != null && obj.skill_id == null) obj.skill_id = obj.id;
      if (obj.version != null && obj.skill_version == null) {
        obj.skill_version = obj.version;
      }
      if (obj.maker != null && obj.author == null) obj.author = obj.maker;
      return obj;
    }
    return val;
  }, skillManifestObject)
  .superRefine((data, ctx) => {
    if (data.kind === 'cv_custom' && !data.cv_config) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'cv_config is required when kind is cv_custom',
        path: ['cv_config'],
      });
    }
    // tabbio/v2 contract: type-specific required fields (lenient for legacy
    // and foreign manifests that don't declare skill_format).
    if (data.skill_format === SKILL_FORMAT_V2) {
      if (!data.type) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'type (cv | other) is required for tabbio/v2 skills',
          path: ['type'],
        });
      }
      if (data.type === 'cv') {
        for (const key of ['country', 'level', 'layout'] as const) {
          if (data[key] == null) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `${key} is required for tabbio/v2 cv skills`,
              path: [key],
            });
          }
        }
      }
      if (data.type === 'other' && data.category == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'category is required for tabbio/v2 other skills',
          path: ['category'],
        });
      }
    }
  });

export type SkillManifest = z.infer<typeof skillManifestObject>;

// ── Frontmatter extraction + minimal YAML reader ──────────────────────────────

const FRONTMATTER_RE =
  /^﻿?---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n([\s\S]*))?$/;

export interface ExtractedFrontmatter {
  frontmatter: string;
  body: string;
}

/** Splits the leading `---`-fenced YAML block from a SKILL.md document. */
export function extractFrontmatter(
  markdown: string,
): ExtractedFrontmatter | null {
  const match = FRONTMATTER_RE.exec(markdown);
  if (!match) return null;
  return { frontmatter: match[1] ?? '', body: match[2] ?? '' };
}

function stripScalar(raw: string): string | boolean {
  let s = raw.trim();
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    // Double-quoted: try a JSON parse so escaped quotes/newlines written by
    // serializeSkillManifestExtras (JSON.stringify) round-trip; fall back to
    // the old quote-strip for non-JSON escapes.
    try {
      return JSON.parse(s) as string;
    } catch {
      s = s.slice(1, -1);
    }
  } else if (s.length >= 2 && s.startsWith("'") && s.endsWith("'")) {
    s = s.slice(1, -1);
  }
  if (s === 'true') return true;
  if (s === 'false') return false;
  return s;
}

const TOP_KEY_RE = /^([A-Za-z_][\w-]*):(.*)$/;
// `key:` or `key: value`: YAML requires a space (or EOL) after the colon,
// which keeps scalars like `https://example.com` from parsing as mappings.
const NESTED_KEY_RE = /^([A-Za-z_][\w-]*):(?:[ \t]+(.*))?$/;

function indentOf(line: string): number {
  return line.length - line.trimStart().length;
}

function isListItemLine(trimmed: string): boolean {
  return /^-(?:[ \t]|$)/.test(trimmed);
}

/**
 * Parses the indented block that follows a `key:` line with an empty value.
 * Purpose-built for the constructs the Tabbio manifests use: NOT general YAML:
 *  - block list of scalars (`- item`)
 *  - block list of flat mappings (`- id: summary` with deeper `prompt: "..."`
 *    continuation keys)
 *  - one level of nested block mapping (`input: cv` lines) whose values are
 *    scalars or, recursively, another indented block (`cv_config.sections`).
 * Returns the parsed value plus the index of the first unconsumed line.
 */
function parseNestedBlock(
  lines: string[],
  start: number,
  parentIndent: number,
): { value: unknown; next: number } {
  let i = start;
  while (i < lines.length && lines[i].trim() === '') i++;
  if (i >= lines.length || indentOf(lines[i]) <= parentIndent) {
    return { value: '', next: start };
  }
  const blockIndent = indentOf(lines[i]);

  // Block list: scalars and/or flat mappings.
  if (isListItemLine(lines[i].trim())) {
    const items: unknown[] = [];
    let current: Record<string, unknown> | null = null;
    let j = i;
    while (j < lines.length) {
      const l = lines[j];
      if (l.trim() === '') {
        j++;
        continue;
      }
      const indent = indentOf(l);
      if (indent <= parentIndent) break;
      const trimmed = l.trim();
      if (indent === blockIndent && isListItemLine(trimmed)) {
        const inner = trimmed.replace(/^-[ \t]*/, '');
        const mapM = NESTED_KEY_RE.exec(inner);
        if (inner === '') {
          // Bare `-`: mapping starts on the following deeper lines.
          current = {};
          items.push(current);
        } else if (mapM) {
          current = {
            [mapM[1]]: mapM[2] === undefined ? '' : stripScalar(mapM[2]),
          };
          items.push(current);
        } else {
          current = null;
          items.push(stripScalar(inner));
        }
        j++;
        continue;
      }
      // Deeper-indented continuation keys belong to the current mapping item.
      if (indent > blockIndent && current) {
        const contM = NESTED_KEY_RE.exec(trimmed);
        if (contM) {
          current[contM[1]] =
            contM[2] === undefined ? '' : stripScalar(contM[2]);
        }
        j++;
        continue;
      }
      // Anything else indented under the list: skip, unsupported.
      j++;
    }
    return { value: items, next: j };
  }

  // Nested block mapping (one level; values may recurse into a list block).
  if (NESTED_KEY_RE.test(lines[i].trim())) {
    const map: Record<string, unknown> = {};
    let j = i;
    while (j < lines.length) {
      const l = lines[j];
      if (l.trim() === '') {
        j++;
        continue;
      }
      const indent = indentOf(l);
      if (indent <= parentIndent) break;
      if (indent !== blockIndent) {
        // Deeper content not claimed by a recursing key: skip, unsupported.
        j++;
        continue;
      }
      const m = NESTED_KEY_RE.exec(l.trim());
      if (!m) {
        j++;
        continue;
      }
      const rest = m[2]?.trim() ?? '';
      if (rest === '') {
        const child = parseNestedBlock(lines, j + 1, indent);
        map[m[1]] = child.value;
        j = child.next === j + 1 ? j + 1 : child.next;
      } else {
        map[m[1]] = stripScalar(rest);
        j++;
      }
    }
    return { value: map, next: j };
  }

  // Unrecognized indented content: consume nothing; caller skips it.
  return { value: '', next: start };
}

/**
 * Parses the SKILL.md frontmatter block into a plain object. Handles the
 * constructs the manifests actually use; nested maps are not supported (none of
 * the real manifests use them at the top level).
 */
export function parseFrontmatterYaml(src: string): Record<string, unknown> {
  const lines = src.split(/\r?\n/);
  const out: Record<string, unknown> = {};
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    // Only column-0 `key:` lines start a top-level entry.
    if (!line.trim() || line.trimStart().startsWith('#') || /^[ \t]/.test(line)) {
      i++;
      continue;
    }
    const m = TOP_KEY_RE.exec(line);
    if (!m) {
      i++;
      continue;
    }
    const key = m[1];
    const rest = m[2].trim();

    // Block scalar: `|`, `>`, with optional chomping indicators.
    if (/^[|>][+-]?$/.test(rest)) {
      const folded = rest[0] === '>';
      const blockLines: string[] = [];
      let baseIndent: number | null = null;
      let j = i + 1;
      while (j < lines.length) {
        const l = lines[j];
        if (l.trim() === '') {
          blockLines.push('');
          j++;
          continue;
        }
        const indent = l.length - l.trimStart().length;
        if (indent === 0) break;
        if (baseIndent === null) baseIndent = indent;
        blockLines.push(l.slice(baseIndent));
        j++;
      }
      while (blockLines.length && blockLines[blockLines.length - 1] === '') {
        blockLines.pop();
      }
      out[key] = folded
        ? blockLines
            .map((s) => s.trimEnd())
            .join(' ')
            .replace(/[ \t]+/g, ' ')
            .trim()
        : blockLines.join('\n');
      i = j;
      continue;
    }

    // Inline flow array: `[a, b, c]` (may wrap across lines until `]`).
    if (rest.startsWith('[')) {
      let buf = rest;
      let j = i;
      while (!buf.includes(']') && j + 1 < lines.length) {
        j++;
        buf += ' ' + lines[j].trim();
      }
      const inner = buf.slice(buf.indexOf('[') + 1, buf.lastIndexOf(']'));
      out[key] = inner
        .split(',')
        .map((s) => stripScalar(s.trim()))
        .filter((s) => !(typeof s === 'string' && s.length === 0));
      i = j + 1;
      continue;
    }

    // Empty value: block list (`- item`, scalar or flat-mapping items), one
    // level of nested block mapping (`run:` / `cv_config:`), or empty scalar.
    if (rest === '') {
      const parsed = parseNestedBlock(lines, i + 1, 0);
      if (parsed.next > i + 1 || parsed.value !== '') {
        out[key] = parsed.value;
        i = Math.max(parsed.next, i + 1);
        continue;
      }
      // Nothing consumable followed: keep legacy empty-scalar behavior,
      // skipping any unrecognized indented content.
      let j = i + 1;
      while (
        j < lines.length &&
        (lines[j].trim() === '' || /^[ \t]/.test(lines[j]))
      ) {
        j++;
      }
      out[key] = '';
      i = j;
      continue;
    }

    // Plain scalar.
    out[key] = stripScalar(rest);
    i++;
  }

  return out;
}

// ── Serialization (compose path) ──────────────────────────────────────────────

/**
 * Renders the two-way-skill frontmatter blocks (`kind`, `run`, `cv_config`) as
 * YAML lines for the SKILL.md compose path (composeManifestMarkdown in
 * apps/api appends this inside the `---` fence). Guaranteed to round-trip
 * through `parseFrontmatterYaml` + `skillManifestSchema`: prompts are
 * JSON-quoted so embedded quotes/newlines survive.
 *
 * Returns '' when nothing is set; otherwise newline-joined lines with no
 * trailing newline.
 */
export function serializeSkillManifestExtras(
  cvConfig?: SkillCvConfig | null,
  run?: SkillRunConfig | null,
  kind?: SkillKind | null,
): string {
  const lines: string[] = [];
  if (kind) lines.push(`kind: ${kind}`);
  if (run) {
    lines.push('run:');
    lines.push(`  input: ${run.input}`);
    lines.push(`  output: ${run.output}`);
  }
  if (cvConfig) {
    lines.push('cv_config:');
    lines.push(`  style: ${cvConfig.style}`);
    lines.push(`  color: ${cvConfig.color}`);
    lines.push(`  showPhoto: ${cvConfig.showPhoto}`);
    lines.push('  sections:');
    for (const section of cvConfig.sections) {
      lines.push(`    - id: ${section.id}`);
      if (section.prompt) {
        lines.push(`      prompt: ${JSON.stringify(section.prompt)}`);
      }
    }
  }
  return lines.join('\n');
}

// ── tabbio/v2 helpers ─────────────────────────────────────────────────────────

/** v2 `language` enum → the locale array stored on `Skill.languages`. */
export function deriveSkillLocales(
  language: 'en' | 'ar' | 'bi' | undefined | null,
): string[] {
  if (language === 'ar') return ['ar'];
  if (language === 'bi') return ['en', 'ar'];
  return ['en'];
}

export interface ParsedSkillIcon {
  kind: 'flag' | 'cat' | 'image';
  value: string;
}

/** Parses the v2 `icon` spec: `flag:uae` | `cat:apply` | `image:<asset>`. */
export function parseSkillIconSpec(
  icon: string | undefined | null,
): ParsedSkillIcon | null {
  if (!icon) return null;
  const m = /^(flag|cat|image):(.+)$/.exec(icon.trim());
  if (!m) return null;
  return { kind: m[1] as ParsedSkillIcon['kind'], value: m[2].trim() };
}

/**
 * Curated CV accent palette: MUST stay in sync with COLOR_MAP in
 * @tabbio/tokens (cv-layout.ts). Duplicated here because this package is
 * zero-dep by design; `skillAccentToCvColor` only needs it for nearest-color
 * mapping of the free-hex `layout.accent` values skills ship with.
 */
const CV_ACCENT_PALETTE: Array<[string, string]> = [
  ['PURPLE', '#5A2AFF'],
  ['BLACK', '#0A0A0A'],
  ['BLUE', '#2563EB'],
  ['EMERALD', '#059669'],
  ['RED', '#C8102E'],
  ['ORANGE', '#EA580C'],
];

function hexToRgb(hex: string): [number, number, number] | null {
  let h = hex.replace('#', '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (h.length < 6) return null;
  const n = Number.parseInt(h.slice(0, 6), 16);
  if (Number.isNaN(n)) return null;
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/**
 * Maps a free-hex `layout.accent` to the nearest curated `CVAccentColor` id
 * (the CV renderer only accepts the 6 preset ids). Falls back to BLACK.
 */
export function skillAccentToCvColor(
  accent: string | undefined | null,
): 'BLACK' | 'BLUE' | 'PURPLE' | 'EMERALD' | 'RED' | 'ORANGE' {
  const rgb = accent ? hexToRgb(accent) : null;
  if (!rgb) return 'BLACK';
  let best: string = 'BLACK';
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const [id, hex] of CV_ACCENT_PALETTE) {
    const p = hexToRgb(hex);
    if (!p) continue;
    const distance =
      (rgb[0] - p[0]) ** 2 + (rgb[1] - p[1]) ** 2 + (rgb[2] - p[2]) ** 2;
    if (distance < bestDistance) {
      bestDistance = distance;
      best = id;
    }
  }
  return best as ReturnType<typeof skillAccentToCvColor>;
}

/** v2 `layout.template` → the renderer's `CVLayoutStyle` id. */
export function skillTemplateToCvStyle(
  template: string | undefined | null,
): 'MODERN' | 'CLASSIC' | 'EXECUTIVE' | 'CREATIVE' {
  const upper = (template ?? '').toUpperCase();
  if (
    upper === 'MODERN' ||
    upper === 'CLASSIC' ||
    upper === 'EXECUTIVE' ||
    upper === 'CREATIVE'
  ) {
    return upper;
  }
  return 'MODERN';
}

// ── Public parse API ──────────────────────────────────────────────────────────

export type ParseSkillManifestResult =
  | { success: true; data: SkillManifest; body?: string }
  | { success: false; error: string; issues?: z.ZodIssue[] };

// ── Tier normalization ────────────────────────────────────────────────────────

/** Canonical entitlement tier stored on `Skill.tier`. */
export type SkillTier = 'free' | 'plus' | 'pro';

/**
 * Maps a manifest's FREE-FORM `tier` string to the canonical entitlement tier
 * stored on the `Skill` row.
 *
 * RULE: pick the LOWEST tier at which the skill is usable AT ALL. Real
 * manifests describe per-tier limits inside the string, so any mention of a
 * lower tier wins over a higher one:
 *
 *   "Free 1/day, Plus unlimited"  -> free  (usable on free, just rate-limited)
 *   "Free basic / Plus full"      -> free  (a basic version runs on free)
 *   "Free | Plus | Pro"           -> free
 *   "Plus" / "plus_only"          -> plus  (no free path at all)
 *   "Pro"                         -> pro
 *
 * Precedence: free < plus < pro. We scan for the lowest token present.
 * `"mixed"` and any string with no recognizable token fall back to `free`
 * (the most permissive Default-shelf default: e.g. `li-optimize: mixed`,
 * whose own description gives free users 1 single-section/day).
 */
export function normalizeSkillTier(rawTier: string | null | undefined): SkillTier {
  const tier = String(rawTier ?? '').toLowerCase();
  // Lowest token present wins (free < plus < pro).
  if (tier.includes('free')) return 'free';
  if (tier.includes('plus')) return 'plus';
  if (tier.includes('pro')) return 'pro';
  // "mixed" / unknown -> lowest usable tier on the Default shelf.
  return 'free';
}

/**
 * Validates a SKILL.md manifest. Accepts either the raw markdown document
 * (frontmatter is extracted + parsed) or an already-parsed frontmatter object.
 */
export function parseSkillManifest(
  input: string | Record<string, unknown>,
): ParseSkillManifestResult {
  let raw: Record<string, unknown>;
  let body: string | undefined;

  if (typeof input === 'string') {
    const extracted = extractFrontmatter(input);
    if (!extracted) {
      return { success: false, error: 'No SKILL.md YAML frontmatter found' };
    }
    raw = parseFrontmatterYaml(extracted.frontmatter);
    body = extracted.body;
  } else {
    raw = input;
  }

  const result = skillManifestSchema.safeParse(raw);
  if (!result.success) {
    return {
      success: false,
      error: result.error.issues
        .map((issue) => `${issue.path.join('.') || '(root)'}: ${issue.message}`)
        .join('; '),
      issues: result.error.issues,
    };
  }

  return { success: true, data: result.data as SkillManifest, body };
}
